import { useState, useCallback } from "react";
import { useBuildingPersistence } from "@/three/hooks/useBuildingPersistence";
import type { BuildingStructureBox } from "@/utils/supabase";

// 빈티지 스타일 정의
const VINTAGE_STYLES = {
  primaryButton: {
    width: "100%",
    padding: "10px",
    fontSize: "14px",
    fontWeight: 700,
    fontFamily: "'Pretendard GOV Variable','Noto Serif KR', 'Georgia', serif",
    background: "#8b7355",
    border: "none",
    borderRadius: "4px",
    color: "#fff",
    cursor: "pointer",
    boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
    transition: "transform 0.1s, background 0.2s",
  },
  secondaryButton: {
    width: "100%",
    padding: "8px",
    fontSize: "13px",
    fontWeight: 500,
    fontFamily: "'Pretendard GOV Variable','Noto Serif KR', 'Georgia', serif",
    background: "transparent",
    border: "1px solid #d4c9b0",
    borderRadius: "4px",
    color: "#666655",
    cursor: "pointer",
    marginTop: "8px",
  },
  error: {
    background: "#fdf2f2",
    border: "1px solid #fbd5d5",
    color: "#c81e1e",
    padding: "8px",
    borderRadius: "4px",
    fontSize: "12px",
    marginBottom: "12px",
    fontFamily: "'Pretendard GOV Variable', sans-serif",
  },
};

interface EditorBox {
  id: string;
  position: [number, number, number];
  scale: [number, number, number];
}

interface EditorActionButtonsProps {
  boxes: EditorBox[];
  buildingName: string;
  buildingText: string;
  gridX: number;
  gridZ: number;
  onClose: () => void;
  onBuildingCreated?: () => void;
  /** 모드: create (신규 생성) / remodel (기존 수정) */
  mode?: "create" | "remodel";
  /** 리모델링 시 대상 건물 ID */
  buildingId?: string;
}

/**
 * 에디터 액션 버튼 - 빈티지 스타일
 */
export function EditorActionButtons({
  boxes,
  buildingName,
  buildingText,
  gridX,
  gridZ,
  onClose,
  onBuildingCreated,
  mode = "create",
  buildingId,
}: EditorActionButtonsProps) {
  const { createBuilding, updateBuilding } = useBuildingPersistence();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 박스 → building_structure 변환
  const boxesToStructure = useCallback(
    (boxes: EditorBox[]): BuildingStructureBox[] => {
      return boxes.map((box) => ({
        position: [
          box.position[0] + box.scale[0] / 2,
          box.position[1] + box.scale[1] / 2,
          box.position[2] + box.scale[2] / 2,
        ] as [number, number, number],
        scale: [...box.scale] as [number, number, number],
      }));
    },
    []
  );

  // 건물 생성 핸들러
  const handleCreateBuilding = useCallback(async () => {
    if (boxes.length === 0) {
      setError("최소 하나의 박스가 필요합니다.");
      return;
    }

    const hasGroundBox = boxes.some((box) => Math.abs(box.position[1]) < 0.01);
    if (!hasGroundBox) {
      setError("최소 하나의 박스가 바닥에 닿아야 합니다.");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const structure = boxesToStructure(boxes);
      const meshIndex = 99 + Math.floor(Math.random() * 100);

      const result = await createBuilding(
        {
          position_x: gridX,
          position_y: 0,
          position_z: gridZ,
          mesh_index: meshIndex,
          building_text: buildingText.slice(0, 50),
          title: buildingName,
          building_structure: structure,
        },
        meshIndex
      );

      if (result) {
        onBuildingCreated?.();
        onClose();
      } else {
        setError("건물 생성에 실패했습니다.");
      }
    } catch (err) {
      setError("오류가 발생했습니다.");
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  }, [
    boxes,
    buildingName,
    buildingText,
    gridX,
    gridZ,
    createBuilding,
    boxesToStructure,
    onBuildingCreated,
    onClose,
  ]);

  // 리모델링 핸들러
  const handleRemodel = useCallback(async () => {
    if (!buildingId) {
      setError("건물 ID가 없습니다.");
      return;
    }

    if (boxes.length === 0) {
      setError("최소 하나의 박스가 필요합니다.");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const structure = boxesToStructure(boxes);

      const success = await updateBuilding(buildingId, {
        building_structure: structure,
        building_text: buildingText.slice(0, 50),
        title: buildingName,
      });

      if (success) {
        onBuildingCreated?.();
        onClose();
      } else {
        setError("리모델링에 실패했습니다.");
      }
    } catch (err) {
      setError("오류가 발생했습니다.");
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  }, [
    buildingId,
    boxes,
    buildingName,
    buildingText,
    updateBuilding,
    boxesToStructure,
    onBuildingCreated,
    onClose,
  ]);

  const handleSubmit =
    mode === "remodel" ? handleRemodel : handleCreateBuilding;
  const buttonText =
    mode === "remodel"
      ? isSubmitting
        ? "리모델링 중..."
        : "리모델링 승인"
      : isSubmitting
      ? "승인 중..."
      : "건물 생성 승인";

  return (
    <div className="pt-2">
      {error && <div style={VINTAGE_STYLES.error}>{error}</div>}

      <button
        onClick={handleSubmit}
        disabled={isSubmitting || boxes.length === 0}
        style={{
          ...VINTAGE_STYLES.primaryButton,
          background: isSubmitting || boxes.length === 0 ? "#ccc" : "#8b7355",
          cursor:
            isSubmitting || boxes.length === 0 ? "not-allowed" : "pointer",
        }}
      >
        {buttonText}
      </button>

      <button onClick={onClose} style={VINTAGE_STYLES.secondaryButton}>
        취소
      </button>
    </div>
  );
}
